<template>
  <div class="flex flex-col glass-frosted rounded-8xl p-4 relative box-border">
    <div class="flex items-center font-bold">
      <div class="flex items-center mr-1 text-l-medium text-black-white">
        <Icon icon="LightEffect-Wireframe-DecorLight" class="size-6" />
        {{ $t('lighting.lightingDirection') }}
      </div>
      <div class="flex-1"></div>
      <Button
        v-for="item in directionList"
        :key="item.value"
        size="xs"
        color="glass"
        :active="modelValue === item.value"
        class="flex items-center mr-2 px-2"
        shape="corner"
        @click="item.fn"
      >
        <Icon :icon="item.icon" class="size-3" />
        {{ item.label }}
      </Button>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue'
  import { type LightingEffectDefinition } from '@rapoo/device-sdk'
  import { Icon, Button } from '@rapoo/element'
  import { useLocaleStore } from '@/stores'

  const props = withDefaults(
    defineProps<{
      /** 0=正向，1=反向 */
      modelValue: number
      /** 当前编辑区灯效；按灯效展示具体的方向选项（横向/纵向波浪、中心扩散、中心回旋） */
      currentEffectInfo?: Pick<LightingEffectDefinition, 'i18nKey'> | null
    }>(),
    { currentEffectInfo: null },
  )

  const emit = defineEmits(['update:modelValue', 'change'])

  const { t } = useLocaleStore()

  /** 按灯效返回其特有方向选项文案；未匹配的兜底为通用的 正向/反向。 */
  const options = computed(() => {
    const key = props.currentEffectInfo?.i18nKey ?? ''
    if (key.endsWith('wave_upr')) {
      return {
        forward: t('lighting.directionTopToBottom'),
        reverse: t('lighting.directionBottomToTop'),
      }
    }
    if (key.endsWith('wave')) {
      return {
        forward: t('lighting.directionLeftToRight'),
        reverse: t('lighting.directionRightToLeft'),
      }
    }
    if (key.endsWith('fountain')) {
      return {
        forward: t('lighting.directionCenterToOuter'),
        reverse: t('lighting.directionOuterToCenter'),
      }
    }
    if (key.endsWith('vortex')) {
      return {
        forward: t('lighting.directionCounterClockwise'),
        reverse: t('lighting.directionClockwise'),
      }
    }
    return { forward: t('lighting.forward'), reverse: t('lighting.reverse') }
  })

  const directionList = computed(() => [
    {
      value: 0,
      label: options.value.forward,
      icon: 'LightEffect-Wireframe-BacklightSwitch',
      fn: () => {
        emit('update:modelValue', 0)
        emit('change', 0)
      },
    },
    {
      value: 1,
      label: options.value.reverse,
      icon: 'LightEffect-Wireframe-SideLightSwitch',
      fn: () => {
        emit('update:modelValue', 1)
        emit('change', 1)
      },
    },
  ])
</script>
